import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  useSingleAdsActivity,
  useUpdateCampaignStatus,
  useAddAdsActivityUpdate,
  useUpdateAdsActivityStatus,
} from '../../shared/hooks/UseAdsActivities';
import CampaignChecklist from './_components/CampaignChecklist';
import ActivityUpdates from './_components/ActivityUpdates';
import Header from '../../components/template/Header';
import BreadCrumb from '../../components/template/BreadCrumb';
import Spinner from '../../components/layout/Spinner';
import toast from 'react-hot-toast';
import moment from 'moment';

const AdsActivityDetails = () => {
  const { id } = useParams();
  const [{ activity, loading, error }, refetch] = useSingleAdsActivity(id);
  const [updateCampaignStatus, { loading: updateLoading }] =
    useUpdateCampaignStatus();
  const [addUpdate, { loading: commentLoading }] = useAddAdsActivityUpdate();
  const [updateStatus, { loading: statusLoading }] =
    useUpdateAdsActivityStatus();

  const [newComment, setNewComment] = useState('');
  const [showCommentForm, setShowCommentForm] = useState(false);

  if (loading) return <Spinner />;
  if (error)
    return <div className='alert alert-danger'>Error loading activity</div>;
  if (!activity)
    return <div className='alert alert-warning'>Activity not found</div>;

  const handleCampaignUpdate = async (
    campaignId,
    status,
    notes,
    actionTaken
  ) => {
    try {
      await updateCampaignStatus(id, campaignId, {
        status,
        notes,
        action_taken: actionTaken,
      });
      toast.success('Campaign updated successfully');
    } catch (error) {
      toast.error('Failed to update campaign');
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      await addUpdate(id, newComment);
      setNewComment('');
      setShowCommentForm(false);
      toast.success('Comment added successfully');
      // Manually refetch to ensure fresh data
      await refetch();
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await updateStatus(id, newStatus);
      toast.success('Status updated successfully');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'warning';
      case 'IN_PROGRESS':
        return 'info';
      case 'COMPLETED':
        return 'success';
      case 'CANCELLED':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const progressPercentage =
    activity.total_campaigns > 0
      ? Math.round(
          (activity.completed_campaigns / activity.total_campaigns) * 100
        )
      : 0;

  return (
    <div className='pace-done'>
      <div>
        <Header />
        <BreadCrumb
          title={activity.title}
          mainLinkTitle='Ads Activities'
          mainLinkUrl='/ads-activities'
          activeLink='Details'
        />

        <div className='container-fluid' style={{ padding: '20px' }}>
          {/* Header */}
          <div className='row mb-4'>
            <div className='col-12'>
              <div className='d-flex justify-content-between align-items-start'>
                <div>
                  <h2 className='page-title mb-2'>{activity.title}</h2>
                  <div className='d-flex gap-2 mb-3'>
                    <span
                      className={`badge bg-${getStatusColor(activity.status)}`}
                    >
                      {activity.status}
                    </span>
                    <span
                      className={`badge bg-${
                        activity.priority === 'HIGH'
                          ? 'danger'
                          : activity.priority === 'MEDIUM'
                          ? 'warning'
                          : 'success'
                      }`}
                    >
                      {activity.priority}
                    </span>
                    <span className='badge bg-primary'>
                      <i
                        className={`${
                          activity.platform === 'FACEBOOK'
                            ? 'fab fa-facebook'
                            : 'fab fa-google'
                        } me-1`}
                      ></i>
                      {activity.platform}
                    </span>
                  </div>
                </div>

                <div className='d-flex gap-2'>
                  {activity.status !== 'COMPLETED' && (
                    <button
                      className='btn btn-success'
                      onClick={() => handleStatusChange('COMPLETED')}
                      disabled={statusLoading}
                    >
                      {statusLoading ? (
                        <i className='fas fa-spinner fa-spin me-1'></i>
                      ) : (
                        <i className='fas fa-check me-1'></i>
                      )}
                      Mark Complete
                    </button>
                  )}
                  <Link
                    to={`/ads-activities/${id}/edit`}
                    className='btn btn-outline-primary'
                  >
                    <i className='fas fa-edit me-1'></i>
                    Edit
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className='row'>
            {/* Main Content */}
            <div className='col-lg-8'>
              {/* Activity Info */}
              <div className='card mb-4'>
                <div className='card-header'>
                  <h5 className='mb-0'>
                    <i className='fas fa-info-circle me-2'></i>
                    Activity Information
                  </h5>
                </div>
                <div className='card-body'>
                  <div className='row'>
                    <div className='col-md-6'>
                      <div className='mb-3'>
                        <strong>Client:</strong>
                        <div>{activity.client?.name}</div>
                      </div>
                      <div className='mb-3'>
                        <strong>Assigned to:</strong>
                        <div>{activity.assigned_to?.name}</div>
                      </div>
                      {activity.assigned_by && (
                        <div className='mb-3'>
                          <strong>Assigned by:</strong>
                          <div>{activity.assigned_by?.name}</div>
                        </div>
                      )}
                    </div>
                    <div className='col-md-6'>
                      {activity.due_date && (
                        <div className='mb-3'>
                          <strong>Due Date:</strong>
                          <div
                            className={
                              moment(activity.due_date).isBefore(moment())
                                ? 'text-danger'
                                : ''
                            }
                          >
                            {moment(activity.due_date).format(
                              'MMM DD, YYYY HH:mm'
                            )}
                            {moment(activity.due_date).isBefore(moment()) && (
                              <i className='fas fa-exclamation-triangle ms-1 text-danger'></i>
                            )}
                          </div>
                        </div>
                      )}
                      <div className='mb-3'>
                        <strong>Created:</strong>
                        <div>
                          {moment(activity.createdAt).format(
                            'MMM DD, YYYY HH:mm'
                          )}
                        </div>
                      </div>
                      {activity.completed_at && (
                        <div className='mb-3'>
                          <strong>Completed:</strong>
                          <div>
                            {moment(activity.completed_at).format(
                              'MMM DD, YYYY HH:mm'
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {activity.description && (
                    <div className='mt-3'>
                      <strong>Description:</strong>
                      <div className='mt-2'>{activity.description}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Progress */}
              <div className='card mb-4'>
                <div className='card-header'>
                  <h5 className='mb-0'>
                    <i className='fas fa-chart-line me-2'></i>
                    Progress
                  </h5>
                </div>
                <div className='card-body'>
                  <div className='d-flex justify-content-between align-items-center mb-3'>
                    <span>Campaign Progress</span>
                    <span className='fw-bold'>
                      {activity.completed_campaigns}/{activity.total_campaigns}{' '}
                      completed
                    </span>
                  </div>
                  <div className='progress mb-2' style={{ height: '10px' }}>
                    <div
                      className={`progress-bar bg-${
                        progressPercentage === 100 ? 'success' : 'info'
                      }`}
                      role='progressbar'
                      style={{ width: `${progressPercentage}%` }}
                    ></div>
                  </div>
                  <small className='text-muted'>
                    {progressPercentage}% complete
                  </small>
                </div>
              </div>

              {/* Campaign Checklist */}
              <div className='card mb-4'>
                <div className='card-header'>
                  <h5 className='mb-0'>
                    <i className='fas fa-tasks me-2'></i>
                    Campaign Checklist
                  </h5>
                </div>
                <div className='card-body'>
                  <CampaignChecklist
                    campaigns={activity.campaigns || []}
                    onUpdate={handleCampaignUpdate}
                    loading={updateLoading}
                    activityId={id}
                  />
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className='col-lg-4'>
              {/* Quick Actions */}
              <div className='card mb-4'>
                <div className='card-header'>
                  <h6 className='mb-0'>Quick Actions</h6>
                </div>
                <div className='card-body'>
                  <div className='d-grid gap-2'>
                    {activity.status !== 'IN_PROGRESS' &&
                      activity.status !== 'COMPLETED' && (
                        <button
                          className='btn btn-info btn-sm'
                          onClick={() => handleStatusChange('IN_PROGRESS')}
                          disabled={statusLoading}
                        >
                          <i className='fas fa-play me-1'></i>
                          Start Working
                        </button>
                      )}

                    <button
                      className='btn btn-outline-primary btn-sm'
                      onClick={() => setShowCommentForm(!showCommentForm)}
                    >
                      <i className='fas fa-comment me-1'></i>
                      Add Comment
                    </button>
                  </div>

                  {/* Comment Form */}
                  {showCommentForm && (
                    <form onSubmit={handleAddComment} className='mt-3'>
                      <div className='mb-3'>
                        <textarea
                          className='form-control'
                          rows='3'
                          placeholder='Add your comment...'
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          required
                        ></textarea>
                      </div>
                      <div className='d-flex gap-2'>
                        <button
                          type='submit'
                          className='btn btn-primary btn-sm'
                          disabled={commentLoading}
                        >
                          {commentLoading ? (
                            <i className='fas fa-spinner fa-spin me-1'></i>
                          ) : (
                            <i className='fas fa-save me-1'></i>
                          )}
                          Save Comment
                        </button>
                        <button
                          type='button'
                          className='btn btn-outline-secondary btn-sm'
                          onClick={() => {
                            setShowCommentForm(false);
                            setNewComment('');
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>

              {/* Updates/Comments */}
              <div className='card'>
                <div className='card-header'>
                  <h6 className='mb-0'>
                    <i className='fas fa-comments me-2'></i>
                    Updates & Comments
                  </h6>
                </div>
                <div className='card-body'>
                  <ActivityUpdates updates={activity.updates || []} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdsActivityDetails;
